import { createStrictOptionalContext } from 'helpers/context';
import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useResettableState } from 'hooks/useResettableState';
import { useInputForm } from '../../dataCreationForms/withInputForm';
import {
  buildUploaderTargetPath,
  canAutoOpenFromCurrentPath,
  DEFAULT_FILE_PREFIX,
  DOCUMENTATION_FILE_PREFIX,
  getRouteFileId,
  isPathInUploaderScope,
  isPathRelatedToUploaderBase,
  isSamePath,
  matchesUploaderFileToRoute,
  removeTrailingSlash,
  resolveAlternateFilePrefix,
  resolveFilePrefix,
} from './routePath';
import Presentational from './presentational';
import { sortFilesWithMainImageFirst } from './sortFilesWithMainImageFirst';
import { FileType, GUProps, ContextType, GU } from './types';

const {
  Provider: GraphicUploaderContextProvider,
  useRequiredContext: useGraphicUploaderContext,
} = createStrictOptionalContext<ContextType>(
  'useGraphicUploaderContext',
  'GraphicUploader'
);

export { useGraphicUploaderContext };

export const WithGraphicUploaderContext: React.FC<{
  value: ContextType;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <GraphicUploaderContextProvider value={value}>
    {children}
  </GraphicUploaderContextProvider>
);

export const GraphicUploader: React.FC<GUProps> = ({
  filesStore,
  addFile,
  editFile,
  removeFile,
  rotateFile,
  drawings,
  addDrawing,
  config,
  classes,
  forwardedRef,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams<Record<string, string>>();
  const { internalEntityBaseUrl, filePrefix, autoOpenCheck } = config;
  const matchedId =
    filePrefix === DOCUMENTATION_FILE_PREFIX
      ? params?.linkedDocumentationVersionId
      : params?.documentId;
  const entityBasePath = internalEntityBaseUrl?.() ?? '';
  // Another entity starts with a closed gallery on its first file.
  const [gallery, setGallery] = useResettableState(entityBasePath, () => ({ slide: 0, open: false }));
  const [wasOpen, setWasOpen] = useResettableState(entityBasePath, () => false);

  const changeGallery: ContextType['changeGallery'] = (place, value) => {
    setGallery((prev) => ({ ...prev, [place]: value }));
  };

  const shouldSyncGalleryWithRoute = Boolean(internalEntityBaseUrl);
  const normalizedPathname = removeTrailingSlash(pathname);

  useEffect(() => {
    if (!gallery.open && matchedId) {
      setWasOpen(false);
    }
  }, [gallery.open, matchedId]);

  useEffect(() => {
    if (!internalEntityBaseUrl || wasOpen || (!matchedId && !autoOpenCheck)) {
      return;
    }

    const baseUrl = internalEntityBaseUrl();
    const activeFilePrefix = resolveFilePrefix(filePrefix);
    const files = filesStore.get();
    const canAutoOpen = canAutoOpenFromCurrentPath(pathname, baseUrl, files, {
      filePrefix: activeFilePrefix,
      matchedId,
      autoOpenCheck,
    });

    if (!canAutoOpen) {
      return;
    }

    const resolveDisplayedFiles = (): FileType[] => {
      const storeFiles = filesStore.get();
      const mainImageHandlerRef = config.mainImageHandlerRef;

      if (!mainImageHandlerRef) {
        return storeFiles;
      }

      const mainImageId = mainImageHandlerRef.current
        .getMainImageStore()
        .get();
      return sortFilesWithMainImageFirst(storeFiles, mainImageId);
    };

    const checkAndOpen = (): boolean => {
      const index = resolveDisplayedFiles().findIndex((file) =>
        matchesUploaderFileToRoute(file, matchedId, baseUrl, {
          filePrefix,
          autoOpenCheck,
        })
      );
      if (index > -1) {
        setGallery({ slide: index, open: true });
        return true;
      }
      return false;
    };

    if (checkAndOpen()) return;
    const unsub = filesStore.subscribe(() => {
      if (checkAndOpen()) unsub();
    });
    return () => unsub();
  }, [
    filesStore,
    internalEntityBaseUrl,
    wasOpen,
    normalizedPathname,
    pathname,
    filePrefix,
    autoOpenCheck,
    matchedId,
    config.mainImageHandlerRef,
  ]);

  useEffect(() => {
    if (!shouldSyncGalleryWithRoute) {
      return;
    }

    const basePath = internalEntityBaseUrl?.();
    if (!basePath) {
      return;
    }

    if (gallery.open) {
      setWasOpen(true);

      const activeFilePrefix = resolveFilePrefix(filePrefix);
      const alternateFilePrefix = resolveAlternateFilePrefix(
        activeFilePrefix
      );

      const isInAlternateUploaderScope = isPathInUploaderScope(
        pathname,
        basePath,
        alternateFilePrefix
      );

      if (isInAlternateUploaderScope) {
        setWasOpen(false);
        setGallery((prev) => (prev.open ? { ...prev, open: false } : prev));
        return;
      }

      const isOnBaseRoute =
        normalizedPathname === removeTrailingSlash(basePath);
      const isInActiveUploaderScope = isPathInUploaderScope(
        pathname,
        basePath,
        activeFilePrefix
      );

      const currentFile = filesStore.get()[gallery.slide];
      if (!currentFile) {
        return;
      }

      const currentRouteFileId = getRouteFileId(currentFile, filePrefix);
      if (!currentRouteFileId) {
        return;
      }

      const nextRoutePath = buildUploaderTargetPath(
        basePath,
        activeFilePrefix,
        currentRouteFileId
      );

      // Sync URL on gallery open (incl. thumbnail click). autoOpenCheck alone is
      // insufficient — it requires /document/ already in pathname. Restrict to
      // routes related to this uploader so we do not hijack navigation after
      // leaving the inspection/template context.
      const canSyncRouteOnOpen =
        isOnBaseRoute ||
        isInActiveUploaderScope ||
        isPathRelatedToUploaderBase(pathname, basePath) ||
        autoOpenCheck?.(basePath, currentFile, activeFilePrefix) === true;

      if (!canSyncRouteOnOpen) {
        return;
      }

      if (!isSamePath(pathname, nextRoutePath)) {
        navigate(nextRoutePath, { replace: true });
      }
      return;
    }

    if (!wasOpen) {
      return;
    }

    setWasOpen(false);

    const files = filesStore.get();
    const wasOnDocumentRoute = canAutoOpenFromCurrentPath(pathname, basePath, files, {
      filePrefix,
      matchedId,
      autoOpenCheck,
    });

    if (!wasOnDocumentRoute) {
      return;
    }

    const targetPath = basePath;
    if (!isSamePath(pathname, targetPath)) {
      navigate(targetPath, { replace: true });
    }
  }, [
    navigate,
    pathname,
    normalizedPathname,
    wasOpen,
    shouldSyncGalleryWithRoute,
    internalEntityBaseUrl,
    filesStore,
    gallery.open,
    gallery.slide,
    filePrefix,
    autoOpenCheck,
  ]);

  useEffect(() => {
    if (!gallery.open) {
      return;
    }

    const unsubscribe = filesStore.subscribe(() => {
      const files = filesStore.get();
      const filesCount = files.length;

      if (filesCount === 0) {
        setGallery({ slide: 0, open: false });
        return;
      }

      if (gallery.slide >= filesCount) {
        setGallery((prev) => ({
          ...prev,
          slide: Math.max(0, filesCount - 1),
        }));
      }
    });

    return unsubscribe;
  }, [filesStore, gallery.open, gallery.slide]);

  const context = {
    filesStore,
    addFile,
    editFile,
    removeFile,
    rotateFile,
    drawings,
    addDrawing,
    config,
    setGallery,
    changeGallery,
    gallery,
    classes,
  };

  if (forwardedRef) {
    forwardedRef.current = context;
  }

  return (
    <GraphicUploaderContextProvider value={context}>
      <Presentational />
    </GraphicUploaderContextProvider>
  );
};

export const InputFormGraphicUploader: React.FC<{
  uploaderKey: string;
  GU: GU;
}> = ({ uploaderKey, GU }) => {
  const { registerUploader, unregisterUploader } = useInputForm();

  useEffect(() => {
    registerUploader(uploaderKey, GU);
    return () => unregisterUploader(uploaderKey);
  }, [uploaderKey, registerUploader, unregisterUploader, GU]);

  return <GraphicUploader {...GU} />;
};
